import { ChevronDown, ChevronUp, PortfolioBalanceIconInheritFill } from 'assets/icons';
import Dropdown, { DropdownItem } from 'components/Dropdown';
import { AppContext } from 'context/ContextProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SettingsEventTypes } from 'types/AppType';
import { NavigationPath } from 'types/DomainTypes';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { useFetchCashBalance } from 'views/shared/hooks/useFetchCashBalance';

interface IPortfolioCard {
  t: TFunction<'translation', undefined>;
  portfolioName?: string;
  currentHoldings?: string;
  balance?: string;
  showBorder?: boolean;
  classname?: string;
  isTotalText?: boolean;
  isDropdownItem?: boolean;
  disableOnClick?: boolean;
}

interface IPortfolioSelector {
  disableOnClick?: boolean;
}

const PortfolioCard = ({
  t,
  balance,
  currentHoldings,
  portfolioName,
  showBorder,
  classname,
  isTotalText,
  isDropdownItem,
  disableOnClick,
}: IPortfolioCard) => {
  const styles = `font-medium  items-center gap-1 ${isDropdownItem ? 'flex' : 'hidden sm:flex'}`;
  return (
    <div
      onClick={(e) => {
        if (disableOnClick) e.stopPropagation();
      }}
      className={`flex flex-col text-black  text-left text-sm  ${
        showBorder ? 'border-b border-b-[#ff906d]' : ''
      } ${classname}`}
    >
      <p className="font-medium whitespace-break-spaces text-[14px]">{portfolioName}</p>

      <div className={styles}>
        <p>{isTotalText ? t('common:totalPortfolioValue') : t('common:portfolioValue')}: </p>
        <p className="font-normal">{currentHoldings}</p>
      </div>

      <div className={styles}>
        <p>{isTotalText ? t('common:totalAvailableBalance') : t('common:availableBalance')}: </p>
        <p className="font-normal"> {balance}</p>
      </div>
    </div>
  );
};

const PortfolioSelector = () => {
  const {
    state: {
      settings: { selectedPortfolioId },
    },
    dispatch,
    formatter: currencyFormatter,
    gbpToTargetCurrency,
  } = useContext(AppContext);

  const { managementFeeUpdates14758 } = useFlags();
  const { t } = useTranslation();
  const location = useLocation();

  const { data: cashBalanceResponse, isLoading: loadingCashBalances } = useFetchCashBalance();
  const { portfolioBalances, loading: loadingBalances } = usePortfolioBalances();

  const disableOnClick = ![NavigationPath.MY_PORTFOLIO, NavigationPath.MY_CELLAR].includes(
    location.pathname as NavigationPath,
  );

  const [arrowDirections, setArrowDirections] = useState(false);

  const portfoliosItems = useMemo(() => {
    if (loadingBalances || loadingCashBalances) return { items: [], dropdown: [] };
    const items = (portfolioBalances ?? []).map((x, index) => {
      const balance = String(
        currencyFormatter.format(
          cashBalanceResponse?.balances?.find((q) => String(q?.portfolioId ?? '') === String(x?.portfolioId ?? ''))
            ?.balance ?? 0,
          true,
        ),
      );
      const currentHoldings = String(gbpToTargetCurrency.convertAndFormat(x.currentHoldings ?? 0));
      const portfolioName = String(x.portfolioName);
      const portfolioId = String(x?.portfolioId ?? '');
      const showBorder = index !== portfolioBalances.length - 1;
      const isTotalText = index === 0;

      return {
        portfolioId,
        balance,
        portfolioName,
        currentHoldings,
        showBorder,
        isTotalText,
      };
    });

    const dropdown = (items ?? []).map((x) => {
      const { balance, currentHoldings, portfolioId, portfolioName, isTotalText, showBorder } = x;

      return {
        id: portfolioId,
        value: balance,
        text: portfolioName,
        content: (
          <PortfolioCard
            disableOnClick={disableOnClick}
            t={t}
            portfolioName={portfolioName}
            balance={balance}
            showBorder={showBorder}
            currentHoldings={currentHoldings}
            isTotalText={isTotalText}
            classname="py-3"
            isDropdownItem={true}
          />
        ),
      };
    });

    return { items, dropdown };
  }, [
    cashBalanceResponse,
    loadingBalances,
    t,
    portfolioBalances,
    currencyFormatter,
    gbpToTargetCurrency,
    loadingCashBalances,
    disableOnClick,
  ]);

  const seletedPortfolio = useMemo(() => {
    const portfolioInfo =
      portfoliosItems.items.find((x) => x.portfolioId === String(selectedPortfolioId)) ?? portfoliosItems.items?.[0];

    return portfolioInfo;
  }, [selectedPortfolioId, portfoliosItems]);

  const onItemSelect = (item: DropdownItem) => {
    setArrowDirections(false);
    dispatch({
      type: SettingsEventTypes.UPDATE_SETTINGS,
      payload: {
        selectedPortfolioId: Number(item.id),
      },
    });
  };

  useEffect(() => {
    if (location.pathname === NavigationPath.PORTFOLIO) {
      dispatch({
        type: SettingsEventTypes.UPDATE_SETTINGS,
        payload: {
          selectedPortfolioId: Number(''),
        },
      });
    }
  }, [location.pathname, dispatch]);

  return (
    <div className="w-full sm:w-[380px]">
      {managementFeeUpdates14758 && (
        <Dropdown
          disabledActiveState={disableOnClick}
          containerClassName="w-full sm:w-[380px]"
          onOpen={() => setArrowDirections(true)}
          onClose={() => setArrowDirections(false)}
          itemsWrapperClassName="w-full overflow-hidden"
          itemClassName={`!py-0 text-base  w-full ${disableOnClick ? '!cursor-default !bg-white' : ''} `}
          className=" text-sm w-full h-16 p-0 justify-start sm:h-24 sm:w-[380px]"
          itemsContainerClassName="w-full !py-0"
          items={portfoliosItems.dropdown}
          onItemSelect={onItemSelect}
        >
          <div className="flex items-center border-black border rounded-xl p-3 text-black">
            <div className="flex flex-row items-center">
              <div className="w-[16px] h-[13px] mr-2">
                <PortfolioBalanceIconInheritFill fill="black" />
              </div>

              <PortfolioCard
                t={t}
                portfolioName={seletedPortfolio?.portfolioName}
                balance={seletedPortfolio?.balance}
                currentHoldings={seletedPortfolio?.currentHoldings}
                isTotalText={seletedPortfolio?.isTotalText}
              />
            </div>

            <div className="ml-auto">
              {!arrowDirections && <ChevronDown className="" aria-hidden="true" />}
              {arrowDirections && <ChevronUp className="" aria-hidden="true" />}
            </div>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export default PortfolioSelector;
